import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { parseISO, eachDayOfInterval, format } from 'date-fns';
import { ChartContainer, type ChartConfig } from '@/shared/components/ui/chart';
import type { MemberTimelineEntry } from '../types';

const MEMBER_COLORS = ['#a855f7', '#14b8a6', '#3b82f6', '#22c55e', '#ec4899'];

interface EfficiencyTrendChartProps {
  entries: MemberTimelineEntry[];
  periodStart: string;
  periodEnd: string;
}

export function EfficiencyTrendChart({
  entries,
  periodStart,
  periodEnd,
}: EfficiencyTrendChartProps) {
  const { t } = useTranslation('laborPerformance');

  const chartConfig = useMemo(() => {
    const cfg: ChartConfig = {};
    entries.forEach((e, i) => {
      cfg[e.memberId] = {
        label: e.memberName.split(' ')[0],
        color: MEMBER_COLORS[i % MEMBER_COLORS.length],
      };
    });
    return cfg;
  }, [entries]);

  const data = useMemo(() => {
    const days = eachDayOfInterval({ start: parseISO(periodStart), end: parseISO(periodEnd) });
    return days.map((d) => {
      const dateStr = format(d, 'yyyy-MM-dd');
      const point: Record<string, number | string | null> = {
        date: dateStr,
        dayLabel: format(d, 'EEE d'),
      };
      entries.forEach((e) => {
        const daily = e.dailyData.find((dd) => dd.date === dateStr);
        point[e.memberId] = daily?.efficiency ?? null;
      });
      return point;
    });
  }, [entries, periodStart, periodEnd]);

  return (
    <div
      className="rounded-lg border bg-card p-4"
      data-testid="labor-performance-efficiency-trend-chart"
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">{t('chart.trendTitle')}</span>
        <span className="text-xs text-muted-foreground">{t('chart.trendSubtitle')}</span>
      </div>
      <ChartContainer config={chartConfig} className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dayLabel" tick={{ fontSize: 11 }} />
            <YAxis
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 11 }}
              domain={[40, 'dataMax']}
            />
            <Tooltip formatter={(v) => [`${v}%`]} />
            <Legend
              formatter={(value) => {
                const entry = entries.find((e) => e.memberId === value);
                return entry?.memberName.split(' ')[0] ?? value;
              }}
            />
            <ReferenceLine
              y={100}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              label={{ value: '100%', position: 'right', fontSize: 10 }}
            />
            {entries.map((e, i) => (
              <Line
                key={e.memberId}
                type="monotone"
                dataKey={e.memberId}
                stroke={MEMBER_COLORS[i % MEMBER_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}
